import { PumpSizingFormData } from "../../types/types";
import { geocodeZip } from "../../requests/geocodeRequest";
import { fetchRainfall } from "../../requests/rainfallRequest";
import { parseNumericValue } from "../helpers/helpers";

const ZIP_PATTERN = /^\d{5}$/;

export function getResolvedGpm(formData: PumpSizingFormData): number {
	const { applicationType, gpmData } = formData;

	if (applicationType === "email" || gpmData.mode === "known") {
		return parseNumericValue(gpmData.knownGpm, 0);
	}

	return parseNumericValue(gpmData.calculatedGpm, 0);
}

function hasCachedSumpRainfall(formData: PumpSizingFormData): boolean {
	const { lat, lon, rainfall } = formData.gpmData.sump;

	return (
		lat.trim() !== "" &&
		lon.trim() !== "" &&
		rainfall.trim() !== "" &&
		Number.parseFloat(rainfall) > 0
	);
}

export function needsSumpRainfallLookup(
	formData: PumpSizingFormData,
): boolean {
	const { applicationType, gpmData } = formData;

	return (
		applicationType === "sump" &&
		gpmData.mode === "help" &&
		!gpmData.sump.manualRainfall &&
		!hasCachedSumpRainfall(formData)
	);
}

export function clearSumpLocationData(
	formData: PumpSizingFormData,
): PumpSizingFormData {
	return {
		...formData,
		gpmData: {
			...formData.gpmData,
			sump: {
				...formData.gpmData.sump,
				lat: "",
				lon: "",
				rainfall: "",
			},
		},
	};
}

/**
 * Geocode ZIP → fetch NOAA rainfall → store lat/lon/rainfall on form data.
 */
export async function resolveSumpRainfall(
	formData: PumpSizingFormData,
): Promise<PumpSizingFormData> {
	const zipCode = formData.gpmData.sump.zipCode.trim();

	if (!ZIP_PATTERN.test(zipCode)) {
		throw new Error("Enter a valid 5-digit US ZIP code.");
	}

	let lat: string;
	let lon: string;

	try {
		({ lat, lon } = await geocodeZip(zipCode));
	} catch (error) {
		throw new Error(
			error instanceof Error
				? error.message
				: "Could not look up that ZIP code. Please try again.",
		);
	}

	if (!lat || !lon) {
		throw new Error(
			"No location found for that ZIP code. Please check it and try again.",
		);
	}

	let rainfall: string;

	try {
		({ rainfall } = await fetchRainfall(lat, lon));
	} catch (error) {
		throw new Error(
			error instanceof Error
				? error.message
				: "Could not fetch rainfall data. Please try again.",
		);
	}

	if (!rainfall || Number.parseFloat(rainfall) <= 0) {
		throw new Error("Could not read a valid rainfall value for this location.");
	}

	return {
		...formData,
		gpmData: {
			...formData.gpmData,
			sump: {
				...formData.gpmData.sump,
				zipCode,
				lat,
				lon,
				rainfall,
			},
		},
	};
}
